import * as React from "react";
import {gql} from "@apollo/client";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {toast} from "react-toastify";
import Loading from "../../components/loading";
import {query} from "../../lib/apollo";
import theme from "../../lib/theme";

const oldUrl = process.env.REACT_APP_OLD_URL;

const LEAD_LOOKUP_Q = gql`
    query LeadLookup($input: LeadLookupInput!) {
        leadLookup(input: $input) {
            leadsFound
            salesTotal
            lead {
                leadId
                comboId
                ipAddress
                firstName
                lastName
                address
                city
                state
                zip
                homePhone
                workPhone
                program
                subject
                educationLevel
                degreeSeeking
                registeredNurse
                email
                military
                classes
                working
                keyword
                publisher
                publisherId
                originalPublisher
                originalPublisherId
                whenStart
                alreadyEnrolled
                highSchoolGradYear
                lastUpdated
                soldCount
                gclid
                creativeUrl
                creativeId
                creativeApproved
                portalUrl
            }
            hpHistory {
                hpCampaignId
                company
                school
                broker
                appId
                dateAdded
                dateSent
                sent
                action
                cpl
                disposition
                ruleActionId
            }
            sfViewHistory {
                hpCampaignId
                company
                school
                broker
                appId
                dateAdded
                skipDate
                preLanderClick
            }
            salesHistory {
                hpCampaignId
                company
                school
                broker
                appId
                date
                rev
            }
        }
    }
`;

type LeadLookupResult = {
    leadsFound: number;
    salesTotal: number;
    lead: any | null;
    hpHistory: any[];
    sfViewHistory: any[];
    salesHistory: any[];
};

const money = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

function DetailRow({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "160px 1fr",
                gap: 1,
                py: 0.6,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": {borderBottom: 0},
            }}
        >
            <Typography variant="body2" sx={{fontWeight: 600, color: "text.secondary"}}>
                {label}
            </Typography>
            <Typography variant="body2" component="div" sx={{wordBreak: "break-word"}}>
                {children}
            </Typography>
        </Box>
    );
}

function HpCampaignLink({id, children}: {id: number; children?: React.ReactNode}) {
    return (
        <Link href={`${oldUrl}/hp_campaign_update.php?hp_campaign_id=${id}`} target="_blank" rel="noreferrer">
            {children ?? id}
        </Link>
    );
}

export default function LeadLookupPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [comboId, setComboId] = React.useState(searchParams.get("combo_id") || "");
    const [email, setEmail] = React.useState(searchParams.get("email") || "");
    const [homePhone, setHomePhone] = React.useState(searchParams.get("home_phone") || "");
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<LeadLookupResult | null>(null);
    const [error, setError] = React.useState("");
    const autoRan = React.useRef(false);

    React.useEffect(() => {
        document.title = "Lookup Lead | DN";
    }, []);

    const runLookup = React.useCallback(
        async (next?: {comboId?: string; email?: string; homePhone?: string}) => {
            const c = (next?.comboId ?? comboId).trim();
            const e = (next?.email ?? email).trim();
            const p = (next?.homePhone ?? homePhone).trim().replace(/\D/g, "");

            const filled = [Boolean(c), Boolean(e), Boolean(p)].filter(Boolean).length;
            if (filled !== 1) {
                toast.error("Enter exactly one of Combo ID, Email, or Home Phone");
                return;
            }
            if (p && p.length !== 10) {
                toast.error("Home phone must be 10 digits");
                return;
            }

            const qs: Record<string, string> = {};
            if (c) qs.combo_id = c;
            if (e) qs.email = e;
            if (p) qs.home_phone = p;
            setSearchParams(qs);

            setLoading(true);
            setError("");
            setResult(null);

            const input: any = {};
            if (c) input.comboId = c;
            else if (e) input.email = e;
            else input.homePhone = p;

            try {
                const response = await query(LEAD_LOOKUP_Q, {input});
                if (!response?.leadLookup) {
                    setError("Lookup failed");
                    return;
                }
                const data = response.leadLookup as LeadLookupResult;
                setResult(data);
                if (!data.lead) {
                    setError("No matching lead found");
                }
            } catch (err: any) {
                setError(err?.message || "Lookup failed");
            } finally {
                setLoading(false);
            }
        },
        [comboId, email, homePhone, setSearchParams],
    );

    React.useEffect(() => {
        if (autoRan.current) return;
        const qsCombo = searchParams.get("combo_id") || "";
        const qsEmail = searchParams.get("email") || "";
        const qsPhone = searchParams.get("home_phone") || "";
        if (!qsCombo && !qsEmail && !qsPhone) return;
        autoRan.current = true;
        void runLookup({comboId: qsCombo, email: qsEmail, homePhone: qsPhone});
    }, [searchParams, runLookup]);

    const lead = result?.lead;

    return (
        <>
            <h2>Lookup Lead</h2>

            <Card variant="outlined" sx={{mb: 3, maxWidth: 960}}>
                <CardContent>
                    <Box
                        component="form"
                        onSubmit={(ev) => {
                            ev.preventDefault();
                            void runLookup();
                        }}
                        sx={{display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start"}}
                    >
                        <TextField
                            label="Combo ID"
                            size="small"
                            value={comboId}
                            onChange={(e) => {
                                setComboId(e.target.value);
                                if (e.target.value) {
                                    setEmail("");
                                    setHomePhone("");
                                }
                            }}
                            disabled={loading}
                            sx={{minWidth: 180}}
                        />
                        <TextField
                            label="Email"
                            size="small"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (e.target.value) {
                                    setComboId("");
                                    setHomePhone("");
                                }
                            }}
                            disabled={loading}
                            sx={{minWidth: 240}}
                        />
                        <TextField
                            label="Home Phone"
                            size="small"
                            value={homePhone}
                            onChange={(e) => {
                                setHomePhone(e.target.value);
                                if (e.target.value) {
                                    setComboId("");
                                    setEmail("");
                                }
                            }}
                            disabled={loading}
                            sx={{minWidth: 160}}
                            inputProps={{inputMode: "numeric"}}
                        />
                        <Button type="submit" variant="contained" disabled={loading} sx={{mt: 0.25}}>
                            Lookup
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {loading && <Loading />}

            {error && !lead && (
                <Alert severity="warning" sx={{mb: 2, maxWidth: 960}}>
                    {error}
                </Alert>
            )}

            {lead && (
                <>
                    <Paper variant="outlined" sx={{p: 2.5, mb: 3, maxWidth: 960}}>
                        <Typography variant="h6" sx={{mb: 1.5}}>
                            Lead Info [{result?.leadsFound} lead(s) found]
                        </Typography>

                        <DetailRow label="Lead ID">
                            {lead.leadId}{" "}
                            (
                            <Link href={`https://prlogs.degreenexus.com/?lead_id=${lead.leadId}`} target="_blank" rel="noreferrer">
                                Stories
                            </Link>
                            ,{" "}
                            <Link
                                href={`https://schools.degreelocate.com/?id=${lead.leadId}${lead.zip}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                CW
                            </Link>
                            )
                        </DetailRow>
                        <DetailRow label="IP">{lead.ipAddress}</DetailRow>
                        <DetailRow label="First Name">{lead.firstName}</DetailRow>
                        <DetailRow label="Last Name">{lead.lastName}</DetailRow>
                        <DetailRow label="Address">{lead.address}</DetailRow>
                        <DetailRow label="City">{lead.city}</DetailRow>
                        <DetailRow label="State">{lead.state}</DetailRow>
                        <DetailRow label="Zip">{lead.zip}</DetailRow>
                        <DetailRow label="Home Phone">{lead.homePhone}</DetailRow>
                        <DetailRow label="Work Phone">{lead.workPhone}</DetailRow>
                        <DetailRow label="Program">
                            {lead.subject ? `${lead.subject} > ${lead.program}` : lead.program}
                        </DetailRow>
                        <DetailRow label="Education Level">{lead.educationLevel}</DetailRow>
                        <DetailRow label="Degree Seeking">{lead.degreeSeeking}</DetailRow>
                        <DetailRow label="Registered Nurse">{lead.registeredNurse}</DetailRow>
                        <DetailRow label="Email">{lead.email}</DetailRow>
                        <DetailRow label="Military">{lead.military}</DetailRow>
                        <DetailRow label="Classes">{lead.classes}</DetailRow>
                        <DetailRow label="Working">{lead.working}</DetailRow>
                        <DetailRow label="Keyword">{lead.keyword}</DetailRow>
                        <DetailRow label="Publisher">
                            {lead.publisher} ({lead.publisherId})
                        </DetailRow>
                        {lead.originalPublisher && (
                            <DetailRow label="OG Publisher">
                                {lead.originalPublisher} ({lead.originalPublisherId})
                            </DetailRow>
                        )}
                        <DetailRow label="When Start">{lead.whenStart}</DetailRow>
                        <DetailRow label="Already Enrolled">{lead.alreadyEnrolled}</DetailRow>
                        <DetailRow label="High School">{lead.highSchoolGradYear}</DetailRow>
                        <DetailRow label="Last Updated">{lead.lastUpdated}</DetailRow>
                        <DetailRow label="Sold Count">{lead.soldCount}</DetailRow>
                        {lead.gclid && <DetailRow label="GCLID">{lead.gclid}</DetailRow>}
                        {lead.creativeUrl && (
                            <DetailRow label="Creative">
                                <Link href={lead.creativeUrl} target="_blank" rel="noreferrer">
                                    {lead.creativeUrl}
                                </Link>{" "}
                                (c_id: {lead.creativeId}, Approved: {lead.creativeApproved ? "Yes" : "No"})
                            </DetailRow>
                        )}
                        {lead.portalUrl && (
                            <DetailRow label="Portal">
                                <Link
                                    href={`https://${lead.portalUrl}?publisher_id=1592`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    https://{lead.portalUrl}?publisher_id=1592
                                </Link>
                            </DetailRow>
                        )}
                    </Paper>

                    <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                        HP History
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{mb: 3, maxWidth: 1200}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["Type", "Company", "School", "Broker", "App Id", "Q Date", "Sent", "Action", "CPL", "Disposition"].map(
                                        (h) => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(result?.hpHistory || []).map((row, idx) => (
                                    <TableRow key={`${row.hpCampaignId}-${idx}`} hover>
                                        <TableCell>
                                            <HpCampaignLink id={row.hpCampaignId}>HP</HpCampaignLink>
                                        </TableCell>
                                        <TableCell>{row.company}</TableCell>
                                        <TableCell>{row.school}</TableCell>
                                        <TableCell>{row.broker}</TableCell>
                                        <TableCell>{row.appId}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.dateAdded}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>
                                            {row.sent === 0 ? "pending" : row.dateSent}
                                        </TableCell>
                                        <TableCell>
                                            {row.sent === 0
                                                ? ""
                                                : !row.action
                                                  ? "None"
                                                  : (
                                                        <Link
                                                            component={RouterLink}
                                                            to={`/campaignConfirms/${row.hpCampaignId}`}
                                                            target="_blank"
                                                        >
                                                            {row.action}
                                                        </Link>
                                                    )}
                                        </TableCell>
                                        <TableCell>{row.sent === 0 ? "$0" : money(row.cpl)}</TableCell>
                                        <TableCell>{row.disposition}</TableCell>
                                    </TableRow>
                                ))}
                                {!result?.hpHistory?.length && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No HP history
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                        SF View History
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{mb: 3, maxWidth: 1200}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["Campaign Id", "Company", "School", "Broker", "App Id", "Date", "Skip", "Pre Lander Click"].map(
                                        (h) => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(result?.sfViewHistory || []).map((row, idx) => (
                                    <TableRow key={`${row.hpCampaignId}-${idx}`} hover>
                                        <TableCell>
                                            <HpCampaignLink id={row.hpCampaignId} />
                                        </TableCell>
                                        <TableCell>{row.company}</TableCell>
                                        <TableCell>{row.school}</TableCell>
                                        <TableCell>{row.broker}</TableCell>
                                        <TableCell>{row.appId}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.dateAdded}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.skipDate}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.preLanderClick}</TableCell>
                                    </TableRow>
                                ))}
                                {!result?.sfViewHistory?.length && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No SF view history
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                        Sales History
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{mb: 3, maxWidth: 1200}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["Campaign Id", "Company", "School", "Broker", "App Id", "Date", "Rev"].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(result?.salesHistory || []).map((row, idx) => (
                                    <TableRow key={`${row.hpCampaignId}-${idx}`} hover>
                                        <TableCell>
                                            <HpCampaignLink id={row.hpCampaignId} />
                                        </TableCell>
                                        <TableCell>{row.company}</TableCell>
                                        <TableCell>{row.school}</TableCell>
                                        <TableCell>{row.broker}</TableCell>
                                        <TableCell>{row.appId}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.date}</TableCell>
                                        <TableCell align="right">{money(row.rev)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={5} sx={{fontWeight: 700}}>
                                        Total
                                    </TableCell>
                                    <TableCell />
                                    <TableCell align="right" sx={{fontWeight: 700}}>
                                        {money(result?.salesTotal || 0)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </>
    );
}
